<template>
  <ContentList
    :columnCollapsed="columnCollapsed"
    v-bind="$attrs"
    :entries="filteredSchemas"
    :loading="loadingByFol || schemasLoading"
    :collapse="collapse"
    :backgroundColor="backgroundColor"
    :titleAsCollapseToggle="titleAsCollapseToggle"
    dividerStyles="max-lg:hidden"
    data-pw="schemas-overview"
    @delete="deleteSchema($event)"
  >
    <template #title>
      <ClipboardIcon v-if="columnCollapsed" class="h-4 w-4" />
      <template v-else>
        <div class="max-lg:w-full max-lg:text-lg max-lg:font-bold">
          {{ $t("legal_text.rightColumn.schemas.title") }}
        </div>
      </template>
    </template>
    <template #action>
      <SchemaFilterMenu
        v-if="!channelsLoading && !columnCollapsed"
        :channels="channels ?? []"
        :schemas="allSchemas"
        small
        @filtered="filteredSchemas = $event"
      />
    </template>
    <template #subtitle>
      <span class="max-lg:font-semibold">{{
        $t("legal_text.rightColumn.schemas.to")
      }}</span>
      {{
        onlyActiveEntryFolSchemas ? activeEntryFol!.name : paragraphFol?.name
      }}
    </template>
    <template #list-header-1>
      {{ $t("legal_text.rightColumn.schemas.to") }}
      {{ activeEntryFol?.name }}
    </template>
    <template #empty-0>
      {{
        onlyActiveEntryFolSchemas
          ? $t("legal_text.rightColumn.schemas.no_schemas_for_entry")
          : $t("legal_text.rightColumn.schemas.no_related_schemas")
      }}
    </template>
    <template #empty-1>
      {{ $t("legal_text.rightColumn.schemas.no_related_schemas") }}
    </template>
    <template #item="{ entry }">
      <EntryLink class="w-full flex-grow overflow-hidden px-1" :entry="entry">
        <template #default="{ active }">
          <SchemaOverviewRow
            :active="active"
            :schema="entry"
            :showIcon="!columnCollapsed"
          />
        </template>
      </EntryLink>
    </template>
  </ContentList>
</template>

<script lang="ts" setup>
import type { IFieldOfLaw } from "~/models/IFieldOfLaw";
import type { ISchema } from "~/models/IUserEntry";

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    columnCollapsed: boolean;
    paragraphFol?: IFieldOfLaw | null;
    collapse?: boolean;
    backgroundColor?: "grey" | "transparent";
    titleAsCollapseToggle?: boolean;
  }>(),
  {
    backgroundColor: "grey",
    paragraphFol: null,
    titleAsCollapseToggle: false,
  }
);

const { open: deleteSchema } = useEntryDeleteModal();

const paragraphFolId = computed(() => props.paragraphFol?.id);

const {
  data: activeParagraphFolSchemas,
  query: { isLoading: loadingByFol },
} = useSchemasForFieldOfLaw(paragraphFolId);

const {
  query: { data: activeEntryFol },
  folId,
} = useActiveEntryFieldOfLaw();

const {
  data: activeEntryFolSchemas,
  query: { isLoading: schemasLoading },
} = useSchemasForFieldOfLaw(folId);

const {
  listQuery: { data: channels, isLoading: channelsLoading },
} = useChannelSubscriptions();

const onlyActiveEntryFolSchemas = computed(
  () =>
    activeEntryFol.value && props.paragraphFol?.id === activeEntryFol.value?.id
);

const allSchemas = computed(() => {
  if (onlyActiveEntryFolSchemas.value) {
    return [activeEntryFolSchemas.value];
  }
  if (!activeEntryFol.value) {
    return [activeParagraphFolSchemas.value];
  }
  return [activeParagraphFolSchemas.value, activeEntryFolSchemas.value];
});

const filteredSchemas = ref<ISchema[][]>(allSchemas.value);
</script>
